import SortOption from '~/constants/common/SortOption';
import type { AppliedFilters } from '~/contracts/common';
import { stripEquals } from '~/utilities/base64';
import decodeCvnaId from '~/utilities/cvnaId';

export type DecodedUrlFilters = {
  filters: Partial<AppliedFilters>;
  hasCvnaId: boolean;
  isVanityPath: boolean;
  page: number;
  sortBy: SortOption;
};

/**
 * Decode an SRP URL into its filter/sort/page state.
 *
 * An SRP URL encodes filters in one of two ways:
 * - canonical: /cars/filters?cvnaid=... — filters live in the cvnaid
 * - vanity:    /cars/sedan — the filter is path-encoded and resolved server-side
 *
 * `decodeCvnaId` only ever reads the cvnaid, so a vanity URL decodes to empty
 * filters. Callers must NOT treat that as the full filter set — use the
 * `isVanityPath` flag to fall back to the server-resolved filters (or leave the
 * current ones untouched) rather than clobbering a path-encoded filter with {}.
 */
export default function decodeUrlFilters(url: URL): DecodedUrlFilters {
  const parsedPage = parseInt(url.searchParams.get('page') ?? '1', 10);
  const hasCvnaId = Boolean(url.searchParams.get('cvnaid'));

  // The filter segment sits right after the last "cars" segment (handles the
  // /dealership/<id>/cars prefix too); /cars/filters is canonical, not vanity.
  // A path with no "cars" segment (e.g. /vehicle/1) is not an SRP
  // vanity URL — guard the lookup so lastIndexOf's -1 doesn't fall through to
  // segments[0] and misclassify it.
  const segments = url.pathname.split('/').filter(Boolean);
  const carsIndex = segments.lastIndexOf('cars');
  const afterCars = carsIndex === -1 ? undefined : segments[carsIndex + 1];
  const isVanityPath = Boolean(afterCars) && afterCars !== 'filters';

  const { filters = {}, sortBy = SortOption.MostPopular } = hasCvnaId
    ? decodeCvnaId(stripEquals(url.searchParams.get('cvnaid') ?? ''))
    : {};

  return {
    filters,
    hasCvnaId,
    isVanityPath,
    page: parsedPage > 1 ? parsedPage : 1,
    sortBy
  };
}
